import { HTMLAttributes } from 'react'

import { QUERY_HEALTH_TOOL } from '@hhgtech/hhg-components/constants'
import dayjs from 'dayjs'
import { FieldPath, FieldValues, RegisterOptions } from 'react-hook-form'
import { IntlFormatters } from 'react-intl'
import * as yup from 'yup'

import {
  CalculateCaloriesProps,
  calculateCalories,
} from 'components/pages/healthTools/helpers'
import { DATE_FORMAT_SSO_SETUP } from 'interfaces/constants'
import {
  ACTIVITY_LEVEL_NAME,
  HealthToolGender,
} from 'interfaces/constants/healthTools'
import { ExtendedNextRouter, LOCALE } from 'interfaces/types'
import { formatGenderUrl } from 'utils/format'

import { BmiFormHelpers } from '.'

export type BmiItemProps = {
  isLoading?: boolean
  isDisabled?: boolean
  dataEventCategory?: string
  dataEventAction?: string
  dataEventLabel?: string
}
export type AdditionalQuestionItem = {
  question: {
    id: number
    value: string
  }
  answer: {
    id: number
    value: string
  }
}
export type BmiFormValue = {
  gender: HealthToolGender
  birthday?: Date
  age?: string
  weight?: string
  height?: string
  bmi?: number
  isCheckYourself?: boolean
  activityLevel?: ACTIVITY_LEVEL_NAME
  additional_questions?: AdditionalQuestionItem[]
  question1?: string
  question2?: string
  newsletter?: boolean
}
export type BmiFormValueKeys = keyof BmiFormValue
export type BmiFormProps = Omit<HTMLAttributes<HTMLDivElement>, 'onSubmit'> & {
  onSubmit?: (value: BmiFormValue) => void
  template?: 'page' | 'embed'
}
export type Rule = Omit<
  RegisterOptions<FieldValues, FieldPath<FieldValues>>,
  'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
>

export function convertLbsToKg(val: number): number {
  return val * 0.45359237
}

export function convertKgToLbs(val: number): number {
  return val / 0.45359237
}

export function convertFtToCm(val: number): number {
  return val * 30.48
}

export function convertCmToFt(val: number): number {
  return val / 30.48
}

export const BmiUnitValidationBase = {
  minAge: 18,
  maxAge: 100,
  minHeight: 1,
  maxHeight: 300,
  minWeight: 1,
  maxWeight: 500,
  heightConvert: (val: number): number => val,
  weightConvert: (val: number): number => val,
}
export type BmiUnitValidate = typeof BmiUnitValidationBase
export const bmiUnitValidation: Record<LOCALE, BmiUnitValidate> = {
  [LOCALE.PhilippinesEnglish]: BmiUnitValidationBase,
  [LOCALE.India]: BmiUnitValidationBase,
  [LOCALE.Indonesia]: BmiUnitValidationBase,
  [LOCALE.Cambodia]: BmiUnitValidationBase,
  [LOCALE.Malaysia]: BmiUnitValidationBase,
  // generate validation rules for MM: weight (lbs) & height (ft)
  // 1(ft) == 30.48(cm)
  // 1(lbs) == 0.45359237(kg)
  [LOCALE.Myanmar]: {
    ...BmiUnitValidationBase,
    minHeight: convertCmToFt(1),
    maxHeight: convertCmToFt(300),
    minWeight: convertKgToLbs(1),
    maxWeight: convertKgToLbs(500),
    heightConvert: convertFtToCm,
    weightConvert: convertLbsToKg,
  },
  [LOCALE.Thailand]: BmiUnitValidationBase,
  [LOCALE.PhilippinesTagalog]: BmiUnitValidationBase,
  [LOCALE.Vietnam]: BmiUnitValidationBase,
  [LOCALE.Taiwan]: BmiUnitValidationBase,
  [LOCALE.Singapore]: BmiUnitValidationBase,
}

// Function calculate bmi from weight unit (Kg) & height unit (cm)
export const getBmi = async (props: CalculateCaloriesProps): Promise<number> =>
  await calculateCalories(props).then((res) => res?.data?.bmi?.bmi || 0)

export const generateSchema = (
  f: IntlFormatters['formatMessage'],
  locale: LOCALE,
) => {
  return yup.object().shape({
    gender: yup.string().required(
      f({
        id: 'tools.genderInput.error',
        defaultMessage: 'Please select gender',
      }),
    ),
    birthday: yup.date().required(
      f({
        id: 'bmi.ageInput.error',
        defaultMessage: 'Please enter a valid age',
      }),
    ),
    // age: yup
    //   .number()
    //   .typeError(
    //     f({
    //       id: 'bmi.ageInput.error',
    //       defaultMessage: 'Please enter a valid age',
    //     }),
    //   )
    //   .required(
    //     f({
    //       id: 'bmi.ageInput.error',
    //       defaultMessage: 'Please enter a valid age',
    //     }),
    //   )
    //   .min(
    //     bmiUnitValidation[locale].minAge,
    //     f({
    //       id: 'tools.ageInput.limitError',
    //       defaultMessage: 'Please enter an age between 18 and 100 years old.',
    //     }),
    //   )
    //   .max(
    //     bmiUnitValidation[locale].maxAge,
    //     f({
    //       id: 'tools.ageInput.limitError',
    //       defaultMessage: 'Please enter an age between 18 and 100 years old.',
    //     }),
    //   ),
    height: yup
      .number()
      .typeError(
        f({
          id: 'bmi.heightInput.error',
          defaultMessage: 'Please enter a valid height',
        }),
      )
      .required(
        f({
          id: 'bmi.heightInput.error',
          defaultMessage: 'Please enter a valid height',
        }),
      )
      .min(
        bmiUnitValidation[locale].minHeight,
        f({
          id: 'bmi.heightInput.error',
          defaultMessage: 'Please enter a valid height',
        }),
      )
      .max(
        bmiUnitValidation[locale].maxHeight,
        f({
          id: 'bmi.heightInput.error',
          defaultMessage: 'Please enter a valid height',
        }),
      ),
    weight: yup
      .number()
      .typeError(
        f({
          id: 'bmi.weightInput.error',
          defaultMessage: 'Please enter a valid weight',
        }),
      )
      .required(
        f({
          id: 'bmi.weightInput.error',
          defaultMessage: 'Please enter a valid weight',
        }),
      )
      .min(
        bmiUnitValidation[locale].minWeight,
        f({
          id: 'bmi.weightInput.error',
          defaultMessage: 'Please enter a valid weight',
        }),
      )
      .max(
        bmiUnitValidation[locale].maxWeight,
        f({
          id: 'bmi.weightInput.error',
          defaultMessage: 'Please enter a valid weight',
        }),
      ),
    question1: yup.string().required(
      f({
        id: 'bmi.question.error',
        defaultMessage: 'We need your input here to continue',
      }),
    ),
    question2: yup.string().required(
      f({
        id: 'bmi.question.error',
        defaultMessage: 'We need your input here to continue',
      }),
    ),
    newsletter: yup.boolean().optional(),
  })
}
export const generateBmiRules: ({
  f,
  locale,
}: {
  f: IntlFormatters['formatMessage']
  locale: LOCALE
}) => Partial<Record<BmiFormValueKeys, { label?: string; rule?: Rule }>> = ({
  f,
  locale,
}) => ({
  gender: {
    label: f({
      id: 'bmi.selectGender',
      defaultMessage: 'What is your gender?',
    }),
    rule: {
      required: f({
        id: 'validation.error.requiredField',
        defaultMessage: 'This field is required',
      }),
    },
  },
  age: {
    label:
      f({
        id: 'bmi.ageInput',
        defaultMessage: 'How old are you?',
      }) +
      ' ' +
      f({
        id: 'bmi.ageInput.desc',
        defaultMessage: '(years)',
      }),
    rule: {
      required: f({
        id: 'validation.error.requiredField',
        defaultMessage: 'This field is required',
      }),
      min: {
        value: bmiUnitValidation[locale].minAge,
        message: f(
          {
            id: 'tools.errorMess.ageRange',
            defaultMessage: 'Please enter age from {min} to {max}',
          },
          {
            min: bmiUnitValidation[locale].minAge,
            max: bmiUnitValidation[locale].maxAge,
          },
        ),
      },
      max: {
        value: bmiUnitValidation[locale].maxAge,
        message: f(
          {
            id: 'tools.errorMess.ageRange',
            defaultMessage: 'Please enter age from {min} to {max}',
          },
          {
            min: bmiUnitValidation[locale].minAge,
            max: bmiUnitValidation[locale].maxAge,
          },
        ),
      },
    },
  },
  height: {
    label: f({
      id: 'bmi.heightInput',
      defaultMessage: 'How tall are you?',
    }),
    rule: {
      required: f({
        id: 'validation.error.requiredField',
        defaultMessage: 'This field is required',
      }),
      min: {
        value: bmiUnitValidation[locale].minHeight,
        message: f({
          id: 'bmi.heightInput.error',
          defaultMessage: 'Please enter a valid height',
        }),
      },
      max: {
        value: bmiUnitValidation[locale].maxHeight,
        message: f({
          id: 'bmi.heightInput.error',
          defaultMessage: 'Please enter a valid height',
        }),
      },
    },
  },
  weight: {
    label: f({
      id: 'bmi.weightInput',
      defaultMessage: 'How much do you weight?',
    }),
    rule: {
      required: f({
        id: 'validation.error.requiredField',
        defaultMessage: 'This field is required',
      }),
      min: {
        value: bmiUnitValidation[locale].minWeight,
        message: f({
          id: 'bmi.weightInput.error',
          defaultMessage: 'Please enter a valid weight',
        }),
      },
      max: {
        value: bmiUnitValidation[locale].maxWeight,
        message: f({
          id: 'bmi.weightInput.error',
          defaultMessage: 'Please enter a valid weight',
        }),
      },
    },
  },
  objective: {
    label: f({
      id: 'bmi.objective',
      defaultMessage: 'Objectives',
    }),
  },
  isCheckYourself: { label: '' },
  bmi: { label: '' },
  activityLevel: {
    label: f({ id: 'bmr.selectActivityLevel' }),
  },
  question1: {
    rule: {
      required: f({
        id: 'validation.error.requiredField',
        defaultMessage: 'This field is required',
      }),
    },
  },
  question2: {
    rule: {
      required: f({
        id: 'validation.error.requiredField',
        defaultMessage: 'This field is required',
      }),
    },
  },
})

export const syncParamsToUseOnSSOForm = (
  data: {
    gender: BmiFormHelpers.BmiFormValue['gender'] | string
    birthday: BmiFormHelpers.BmiFormValue['birthday']
  },
  router: ExtendedNextRouter,
) => {
  const searchParams = new URLSearchParams(window.location.search)
  searchParams.set(QUERY_HEALTH_TOOL.gender, formatGenderUrl(data.gender))
  if (data?.birthday) {
    data?.birthday &&
      searchParams.set(
        QUERY_HEALTH_TOOL.birthday,
        encodeURIComponent(dayjs(data.birthday).format(DATE_FORMAT_SSO_SETUP)),
      )
  } else {
    searchParams.delete(QUERY_HEALTH_TOOL.birthday)
  }
  window.history.replaceState(
    {},
    '',
    `${router.asPath.split('?')[0]}?${searchParams.toString()}`,
  )
}
